import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { applyTheme, readThemeChoice, resolveTheme, saveThemeChoice } from '../theme';

/**
 * Light, dark, or the system's: the reader's choice (`mode`) and what it comes out as on
 * this device (`resolved`). index.html applied it before the first paint; this keeps the
 * document in step with later choices and with the system switching under a 'system' choice.
 */
const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => readThemeChoice());
  const [resolved, setResolved] = useState(() => resolveTheme(mode));

  useEffect(() => {
    const r = resolveTheme(mode);
    setResolved(r);
    applyTheme(r);
    if (mode !== 'system' || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => { const next = resolveTheme('system'); setResolved(next); applyTheme(next); };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [mode]);

  const setTheme = useCallback((choice) => {
    const next = choice === 'light' || choice === 'dark' ? choice : 'system';
    saveThemeChoice(next);
    setMode(next);
  }, []);
  const toggleTheme = useCallback(() => setTheme(resolved === 'dark' ? 'light' : 'dark'), [resolved, setTheme]);
  const useSystemTheme = useCallback(() => setTheme('system'), [setTheme]);

  const value = useMemo(() => ({
    mode,
    resolved,
    theme: resolved,
    isDark: resolved === 'dark',
    isLight: resolved === 'light',
    setTheme,
    toggleTheme,
    useSystemTheme,
  }), [mode, resolved, setTheme, toggleTheme, useSystemTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

/**
 * Outside the provider (the harness, a test rendering one component) the hook still answers,
 * from the document itself: the choice applies at once, with no re-render.
 */
const bare = () => {
  const mode = readThemeChoice();
  const resolved = typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : resolveTheme(mode);
  const setTheme = (choice) => saveThemeChoice(choice === 'light' || choice === 'dark' ? choice : 'system');
  return {
    mode, resolved, theme: resolved, isDark: resolved === 'dark', isLight: resolved === 'light',
    setTheme, toggleTheme: () => setTheme(resolved === 'dark' ? 'light' : 'dark'), useSystemTheme: () => setTheme('system'),
  };
};

export const useThemeMode = () => useContext(ThemeContext) || bare();
